import { useCallback, useEffect, useState } from 'react';
import { fetchGalleryFeed, type GalleryItem, type SortOption } from '../lib/api';
import { OriginalCard } from './OriginalCard';

const PAGE = 12;

const SORTS: { value: SortOption; label: string }[] = [
  { value: 'recent', label: 'New' },
  { value: 'popular', label: 'Hot' },
  { value: 'views', label: 'Trending' },
  { value: 'engagement', label: 'Top' },
];

/** Lowercased fields a creator search matches against. */
function creatorHaystack(item: GalleryItem): string {
  return [item.displayName, item.twitterHandle, item.creator_x_handle]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Originals Feed — the full live browse grid for Explore, ported from MemeGenes'
 * Explore (`MemeCollections initialTab="all"`). Reads the public gallery feed
 * with sort + cursor pagination; creator search filters the loaded items
 * client-side (as MemeGenes does). Fail-open at the client edge: an outage
 * surfaces as the empty state, never a thrown error.
 */
export function OriginalsFeed() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [sort, setSort] = useState<SortOption>('recent');
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  // (Re)load the first page. Re-runs whenever the sort changes.
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    const res = await fetchGalleryFeed({ limit: PAGE, sort });
    if (res.items.length === 0 && !res.hasMore) {
      // Empty page from a fail-open client read is indistinguishable from an
      // outage; treat a wholly empty first load as an error so the user can retry.
      setError(true);
    }
    setItems(res.items);
    setCursor(res.nextCursor);
    setHasMore(res.hasMore);
    setLoading(false);
  }, [sort]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    const res = await fetchGalleryFeed({ limit: PAGE, sort, cursor });
    setItems((prev) => [...prev, ...res.items]);
    setCursor(res.nextCursor);
    setHasMore(res.hasMore);
    setLoadingMore(false);
  }, [cursor, loadingMore, sort]);

  const q = query.trim().toLowerCase();
  const visible = q ? items.filter((it) => creatorHaystack(it).includes(q)) : items;

  return (
    <section className="pt-12">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-2xl font-black tracking-tight">All Originals</h2>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-full border border-provny-border p-1 dark:border-provny-border-dark">
            {SORTS.map((s) => (
              <button
                key={s.value}
                onClick={() => setSort(s.value)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition-colors ${
                  sort === s.value
                    ? 'bg-provny-cyber text-provny-ink'
                    : 'text-provny-muted hover:text-provny-black dark:hover:text-white'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search creators"
            className="w-44 rounded-full border border-provny-border bg-transparent px-4 py-1.5 text-sm placeholder:text-provny-muted focus:border-provny-cyber focus:outline-none dark:border-provny-border-dark"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : error ? (
        <Notice>
          <p className="text-provny-muted">Couldn't load originals right now.</p>
          <button
            onClick={() => void load()}
            className="rounded-xl bg-provny-cyber px-4 py-2 text-sm font-semibold text-provny-ink transition hover:bg-provny-cyber/90"
          >
            Try again
          </button>
        </Notice>
      ) : visible.length === 0 ? (
        <Notice>
          <p className="text-provny-muted">
            {q ? `No originals from "${query}"` : 'No originals yet.'}
          </p>
        </Notice>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((item) => (
              <OriginalCard key={item.publicMemeId} item={item} />
            ))}
          </div>

          {/* Creator search filters loaded items only; hide Load more while a
              filter is active so it can't appear to "find nothing". */}
          {hasMore && !q && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="rounded-xl border border-provny-border px-6 py-2.5 text-sm font-semibold transition hover:border-provny-cyber hover:text-provny-cyber disabled:opacity-50 dark:border-provny-border-dark"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SkeletonGrid() {
  return (
    <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {Array.from({ length: PAGE }).map((_, i) => (
        <div
          key={i}
          className="aspect-[4/5] animate-pulse rounded-xl bg-provny-border/40 dark:bg-provny-border-dark/40"
        />
      ))}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border border-provny-border py-16 text-center dark:border-provny-border-dark">
      {children}
    </div>
  );
}
